import React, { useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';

const money = (n) => `₹${(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const SearchTab = () => {
  const [q, setQ] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    if (!q.trim()) { toast.error('Enter a design code'); return; }
    setLoading(true);
    try {
      const res = await api.get('/sample-tracker/search', { params: { design_code: q.trim() } });
      setResult(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Search failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <Input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && search()}
              placeholder="Enter a design code (e.g. 1004)…" className="h-9 text-sm" data-testid="search-design-input" />
            <Button onClick={search} className="bg-emerald-600 hover:bg-emerald-700" data-testid="search-design-btn">Search</Button>
          </div>
        </CardContent>
      </Card>

      {loading && <p className="text-sm text-gray-400 px-1">Searching…</p>}

      {result && (
        <div className="space-y-4" data-testid="search-result">
          <Card>
            <CardContent className="p-4 flex items-center gap-3 flex-wrap">
              <h2 className="text-lg font-bold text-gray-900">{result.design_code}</h2>
              {!result.found && <Badge className="bg-amber-50 text-amber-700 border border-amber-200">Not in catalogue</Badge>}
              {result.category && <span className="text-sm text-gray-500">{result.category}</span>}
              <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">Present in {result.present_count}</Badge>
              <Badge className="bg-gray-100 text-gray-600 border border-gray-300">Missing from {result.missing_count}</Badge>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <h3 className="text-sm font-bold text-gray-800 mb-2">🎨 Performance by Colour</h3>
              {(!result.color_breakdown || result.color_breakdown.length === 0) ? (
                <p className="text-sm text-gray-400">No orders recorded for any colour yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="color-breakdown-table">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 border-b">
                        <th className="px-3 py-2 font-semibold">Colour</th>
                        <th className="px-3 py-2 font-semibold">Carried?</th>
                        <th className="px-3 py-2 font-semibold text-right">Events</th>
                        <th className="px-3 py-2 font-semibold text-right">Qty Ordered</th>
                        <th className="px-3 py-2 font-semibold text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.color_breakdown.map(c => (
                        <tr key={c.color} className="border-b border-gray-100">
                          <td className="px-3 py-2 font-medium text-gray-800">{c.color}</td>
                          <td className="px-3 py-2">{c.carried ? <span className="text-emerald-600">Yes</span> : <span className="text-orange-600">No</span>}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{c.event_count}</td>
                          <td className="px-3 py-2 text-right font-semibold">{c.qty}</td>
                          <td className="px-3 py-2 text-right">{money(c.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <h3 className="text-sm font-bold text-gray-800 mb-2">✅ Present in these events</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="present-events-table">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 border-b">
                      <th className="px-3 py-2 font-semibold">Event</th>
                      <th className="px-3 py-2 font-semibold">Type</th>
                      <th className="px-3 py-2 font-semibold">Location</th>
                      <th className="px-3 py-2 font-semibold">Colours carried</th>
                      <th className="px-3 py-2 font-semibold text-right">Orders</th>
                      <th className="px-3 py-2 font-semibold text-right">Qty</th>
                      <th className="px-3 py-2 font-semibold text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.present_events.map(e => (
                      <tr key={e.event_id} className="border-b border-gray-100">
                        <td className="px-3 py-2 font-medium text-gray-800">{e.name}</td>
                        <td className="px-3 py-2 text-gray-600">{e.event_type || '—'}</td>
                        <td className="px-3 py-2 text-gray-600">{e.location || '—'}</td>
                        <td className="px-3 py-2 text-gray-600">{(!e.colors || e.colors.length === 0) ? 'All' : e.colors.join(', ')}</td>
                        <td className="px-3 py-2 text-right">{e.order_count}</td>
                        <td className="px-3 py-2 text-right font-semibold">{e.qty}</td>
                        <td className="px-3 py-2 text-right">{money(e.amount)}</td>
                      </tr>
                    ))}
                    {result.present_events.length === 0 && <tr><td colSpan={7} className="px-3 py-5 text-center text-gray-400">Not carried to any event yet</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <h3 className="text-sm font-bold text-gray-800 mb-2">❌ Missing from these events</h3>
              {result.missing_events.length === 0 ? (
                <p className="text-sm text-gray-400">Carried to all events 🎉</p>
              ) : (
                <div className="flex flex-wrap gap-1.5" data-testid="missing-events-list">
                  {result.missing_events.map(e => (
                    <Badge key={e.event_id} className="bg-gray-50 text-gray-600 border border-gray-200">
                      {e.name}{e.location ? ` · ${e.location}` : ''}
                      {e.order_count > 0 && <span className="ml-1 text-orange-600">({e.qty} ordered)</span>}
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SearchTab;
